import { Button, Grid2, IconButton, Stack, Typography } from "@mui/material";
import { IconWallet, IconX } from "@tabler/icons-react";
import { Input } from "./Input";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { PayableCreateStudent } from "../services/payable";
import { formatPrice, maskMoney, unMaskMoney } from "../helpers/format";
import Loading from "./Loading";
import SelectComponent from "./SelectComponent";
import Info from "../components/Info";
import moment from "moment";

const PaymentCreate = ({ onClose, refetch, studentId }: any) => {
  const [maskedValue, setMaskedValue] = useState("");
  const [paymentType, setPaymentType] = useState<string>("MONEY");
  const [installments, setInstallments] = useState<string>("1");
  const [paymentsPix, setPaymentsPix] = useState<any[]>([]);

  const { mutate, isPending } = useMutation({
    mutationFn: PayableCreateStudent,
    onSuccess: () => {
      onClose?.();
      refetch?.();
      toast.success("Cobrança registrada com sucesso!");
    },
    onError: (e: any) => {
      const message =
        e?.response?.data?.message ??
        e?.message ??
        "Erro ao registrar a cobrança!";
      toast.error(message);
    },
  });

  const { values, handleSubmit, handleChange, setFieldValue } = useFormik({
    initialValues: {
      title: "",
      description: "",
      value: "",
      dueDate: moment().format("YYYY-MM-DD"),
    },
    onSubmit: (vals) => {
      let dueDate: string;

      if (paymentType === "MONEY") {
        dueDate = moment().format("YYYY-MM-DD");
      } else if (paymentType === "PIX_INSTALLMENTS") {
        dueDate = paymentsPix[0]?.dueDate || moment().format("YYYY-MM-DD");
      } else {
        dueDate = vals.dueDate
          ? moment(vals.dueDate).format("YYYY-MM-DD")
          : moment().format("YYYY-MM-DD");
      }

      const value = Number(unMaskMoney(maskedValue));

      const basePayload: any = {
        title: vals.title,
        description: vals.description,
        value: value,
        paymentType,
        installments: Number(installments),
        studentId,
        dueDate,
        paymentsPix,
      };

      mutate(basePayload);
    },
  });

  const total = Number(unMaskMoney(maskedValue)) || 0;
  const maxInstallmentsCreditCard = 6;

  useEffect(() => {
    if (paymentType !== "PIX_INSTALLMENTS" && paymentType !== "CREDIT_CARD")
      return;

    const n = Number(installments) || 1;
    const baseDate = values.dueDate ? moment(values.dueDate) : moment();

    setPaymentsPix(() => {
      return Array.from({ length: n }, (_, i) => ({
        id: i + 1,
        value: parseFloat((total / n).toFixed(2)),
        dueDate: baseDate.clone().add(i, "months").format("YYYY-MM-DD"),
      }));
    });
  }, [installments, paymentType, total, values.dueDate]);

  return (
    <>
      <Loading loading={isPending} />
      <Grid2 container spacing={2} padding={1}>
        <Grid2 size={{ xs: 10 }}>
          <Typography fontSize={24} fontWeight={600}>
            Criar Cobrança
          </Typography>
          <Typography variant="subtitle1" color="secondary.contrastText">
            Preencha os dados da cobrança
          </Typography>
        </Grid2>
        <Grid2 size={{ xs: 2 }}>
          <IconButton onClick={onClose}>
            <IconX />
          </IconButton>
        </Grid2>

        <Grid2 size={{ xs: 12 }} mt={2}>
          <Input
            id="title"
            name="title"
            label="Nome da cobrança"
            placeholder="Ex.: Mensalidade do plano"
            value={values.title}
            onChange={handleChange}
          />
        </Grid2>

        <Grid2 size={{ xs: 12 }}>
          <Input
            id="value"
            name="value"
            label="Valor"
            placeholder="0,00"
            value={formatPrice(maskedValue)}
            onChange={(e) => {
              const masked = maskMoney(e.target.value);
              setMaskedValue(masked);
              const numeric = unMaskMoney(masked);
              setFieldValue("value", numeric);
            }}
            onBlur={() => {
              if (!maskedValue) return;
              const numeric = unMaskMoney(maskedValue);
              setFieldValue("value", numeric);
            }}
          />
        </Grid2>

        <Grid2 size={12}>
          <SelectComponent
            label="Tipo de pagamento"
            placeholder="Escolha o tipo de pagamento"
            value={paymentType}
            onChange={(e: any) => {
              if (e.value === "PIX_BILLET") {
                setInstallments("1");
              }

              setPaymentType(e.value);
            }}
            startIcon={<IconWallet />}
            options={[
              { value: "MONEY", label: "Dinheiro" },
              { value: "PIX_BILLET", label: "PIX à vista" },
              { value: "PIX_INSTALLMENTS", label: "PIX Parcelado" },
              {
                value: "CREDIT_CARD",
                label: `Cartão de crédito até ${maxInstallmentsCreditCard}X`,
              },
            ]}
          />
        </Grid2>

        {paymentType === "PIX_BILLET" && (
          <Grid2 size={{ xs: 12 }}>
            <Input
              id="dueDate"
              name="dueDate"
              label="Data de vencimento"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={values.dueDate}
              onChange={handleChange}
            />
          </Grid2>
        )}

        {paymentType === "CREDIT_CARD" && (
          <Grid2 size={{ xs: 12 }}>
            <Input
              id="dueDate"
              name="dueDate"
              label="Data de vencimento"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={values.dueDate}
              onChange={handleChange}
            />
          </Grid2>
        )}

        {paymentType === "PIX_INSTALLMENTS" && (
          <Grid2 size={12}>
            <Stack
              flexDirection={"column"}
              justifyContent={"space-between"}
              gap={1}
              sx={{
                backgroundColor: "#282828",
                padding: "1rem 0rem 1rem 1rem",
                borderRadius: "1rem",
              }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                PIX Parcelado
              </Typography>

              <Stack width={"320px"}>
                <SelectComponent
                  name="productId"
                  placeholder="Parcelas"
                  value={installments}
                  onChange={(e: any) => {
                    setInstallments(e.value);
                  }}
                  options={Array.from(
                    { length: maxInstallmentsCreditCard },
                    (_, i) => i + 1
                  )
                    .filter((i) => total / Number(i) >= 10)
                    .map((i) => ({
                      value: String(i),
                      label: `Até ${i}x de ${formatPrice(total / i)}`,
                    }))}
                />
              </Stack>
            </Stack>
          </Grid2>
        )}

        <Grid2 container spacing={1} size={12}>
          {installments &&
            total > 0 &&
            paymentType.includes("PIX") &&
            paymentsPix?.map((i: any, index: number) => (
              <Grid2 size={12} key={index}>
                <Stack
                  flexDirection={"row"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                  sx={{
                    backgroundColor: "#282828",
                    padding: "1rem",
                    borderRadius: "1rem",
                  }}
                >
                  <Info
                    size="auto"
                    title="Parcela"
                    subTitle={`${i.id}/${Number(installments)}`}
                  />

                  <Stack>
                    <Input
                      id={`pix-due-${index}`}
                      name={`pix-due-${index}`}
                      type="date"
                      fullWidth
                      value={i.dueDate}
                      onChange={(e) => {
                        const newDate = e.target.value;
                        setPaymentsPix((prev: any) =>
                          prev.map((item: any, idx: any) =>
                            idx === index ? { ...item, dueDate: newDate } : item
                          )
                        );
                      }}
                      onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                        e.target.showPicker()
                      }
                    />
                  </Stack>

                  <Info
                    size="auto"
                    title="Valor"
                    subTitle={formatPrice(i.value)}
                  />
                </Stack>
              </Grid2>
            ))}
        </Grid2>

        {paymentType === "CREDIT_CARD" && (
          <Grid2 size={12}>
            <Stack
              flexDirection={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
              sx={{
                backgroundColor: "#282828",
                padding: "1rem 0rem 1rem 1rem",
                borderRadius: "1rem",
              }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                Cartão de crédito
              </Typography>
              <Stack width={"320px"}>
                <SelectComponent
                  name="productId"
                  placeholder="Parcelas"
                  value={installments}
                  onChange={(e: any) => {
                    setInstallments(e.value);
                  }}
                  options={Array.from(
                    { length: maxInstallmentsCreditCard },
                    (_, i) => i + 1
                  )
                    .filter((i) => total / Number(i) >= 10)
                    .map((i) => ({
                      value: String(i),
                      label: `Até ${i}x de ${formatPrice(total / i)}`,
                    }))}
                />
              </Stack>
            </Stack>
          </Grid2>
        )}

        <Grid2 size={{ xs: 12 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              if (!values.title) {
                toast.info("Preencha o nome da cobrança");
                return;
              }

              if (!values.value) {
                toast.info("Preencha o valor da cobrança ");
                return;
              }

              if (
                paymentType !== "MONEY" &&
                Number(unMaskMoney(values.value)) < 2
              ) {
                toast.info("Preencha um valor maior que R$2,00 ");
                return;
              }

              handleSubmit();
            }}
          >
            Criar Cobrança
          </Button>
        </Grid2>
      </Grid2>
    </>
  );
};

export default PaymentCreate;
